import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PracticeProvider, usePractice } from './context/PracticeContext';
import { TimezoneProvider } from './context/TimezoneContext';
import ProtectedRoute from './components/ProtectedRoute';
import PlatformHeader from './components/PlatformHeader';
import Sidebar from './components/Sidebar';
import CommandPalette from './components/CommandPalette';
import Breadcrumbs from './components/Breadcrumbs';
import AdminDrawer from './components/AdminDrawer';
import { useCommandPalette } from './hooks/useCommandPalette';
import { useAdminDrawer } from './hooks/useAdminDrawer';
import { isNavV2Enabled } from './utils/env';
import ErrorBoundary from './components/ErrorBoundary';

// Pages
import Dashboard from './pages/Dashboard';
import Campaigns from './pages/Campaigns';
import Patients from './pages/Patients';
import Reports from './pages/Reports';
// import Practices from './pages/Practices'; // Replaced by AdminPracticeHub
import ActivityLog from './pages/ActivityLog';
import PracticeSettings from './pages/PracticeSettings';
import IdentityConsole from './pages/IdentityConsole';
import Login from './pages/Login';
import SsoCallback from './pages/SsoCallback';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Setup from './pages/Setup';
import Messaging from './pages/Messaging';
import VoiceAi from './pages/VoiceAi';
// The Voice Flow builder is no longer routed (every call uses the code-owned
// launch prompt since the July 2026 reset); its pages are removed in Phase B.
import Reputation from './pages/Reputation';
import FeedbackLanding from './pages/FeedbackLanding';
import DocumentUploadLanding from './pages/DocumentUploadLanding';
import PatientPayLanding from './pages/PatientPayLanding';
import AiQueue from './pages/AiQueue';
import PatientVoice from './pages/PatientVoice';
import TestBed from './pages/TestBed';
import EHRLiveTest from './pages/EHRLiveTest';
import PublishSlots from './pages/PublishSlots';
import CareHub from './pages/CareHub';
import Onboarding from './pages/Onboarding';
import NotificationCenter from './pages/NotificationCenter';
import Communication from './pages/Communication';
import RevenueFlow from './pages/RevenueFlow';
import ROIDashboard from './pages/ROIDashboard';
import OperationsReadiness from './pages/OperationsReadiness';
import WaitlistManagement from './pages/WaitlistManagement';
import BillingEscalations from './pages/BillingEscalations';
import InsuranceChangeRequests from './pages/InsuranceChangeRequests';
import PaymentSettings from './pages/PaymentSettings';
import PaymentTest from './pages/PaymentTest';
import AmbientScribe from './pages/AmbientScribe';
import AppointmentScribe from './pages/AppointmentScribe';
import SelectContext from './pages/SelectContext';
import SystemLogs from './pages/SystemLogs';
import AiInsights from './pages/AiInsights';
import UserProfile from './pages/UserProfile';
import { useIsMobile } from './hooks/useMediaQuery';
import { AdoptionProvider } from './context/AdoptionContext';
import AdoptionRenderer from './components/adoption/AdoptionRenderer';
import NewtonAI from './components/NewtonAI';

// Admin Pages
// import AdminDashboard from './pages/admin/AdminDashboard'; // Replaced by AdminPracticeHub
// import PracticeGroups from './pages/admin/PracticeGroups'; // Replaced by AdminPracticeHub
import Users from './pages/admin/Users';
import AuditLog from './pages/admin/AuditLog';
import HIPAAAuditLog from './pages/admin/HIPAAAuditLog';
import BAAManagement from './pages/admin/BAAManagement';
import PlatformCompliance from './pages/admin/PlatformCompliance';
import PlatformLogs from './pages/admin/PlatformLogs';
import MigrationReview from './pages/admin/MigrationReview';
import PhoneManagement from './pages/admin/PhoneManagement';
import AdminPracticeHub from './pages/admin/AdminPracticeHub';
import Compliance from './pages/Compliance';
import RiskAssessmentWizard from './pages/compliance/RiskAssessmentWizard';
import CampaignTypes from './pages/admin/CampaignTypes';
import TagManagement from './pages/TagManagement';
import PatientFlow from './pages/PatientFlow';

// Super Admin Layout - Platform Administration
function SuperAdminLayout() {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();
  const { open: paletteOpen, closePalette } = useCommandPalette();

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  return (
    <div className="App" style={styles.appLayout}>
      <CommandPalette open={paletteOpen} onClose={closePalette} />
      <Sidebar
        isAdminView={true}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(c => !c)}
        isMobile={isMobile}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
      />
      <div style={styles.appMain}>
        <PlatformHeader isAdminView={true} isMobile={isMobile} onMenuToggle={() => setMobileMenuOpen(o => !o)} />
        <div className="container" style={styles.mainContent}>
          <AdoptionRenderer />
          {isNavV2Enabled() && <Breadcrumbs />}
          <Routes>
            <Route path="/" element={<AdminPracticeHub />} />
            <Route path="/practice-groups" element={<AdminPracticeHub />} />
            <Route path="/practices" element={<AdminPracticeHub />} />
            <Route path="/users" element={<Users />} />
            <Route path="/identity" element={<IdentityConsole />} />
            <Route path="/audit-log" element={<AuditLog />} />
            <Route path="/hipaa-audit-log" element={<HIPAAAuditLog />} />
            <Route path="/baa" element={<BAAManagement />} />
            <Route path="/compliance" element={<PlatformCompliance />} />
            <Route path="/logs" element={<PlatformLogs />} />
            <Route path="/migration" element={<MigrationReview />} />
            <Route path="/phone-management" element={<PhoneManagement />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </div>
      </div>
      <NewtonAI />
    </div>
  );
}

// Practice Admin Layout - Practice Operations
function PracticeAdminLayout() {
  const { selectedPracticeId } = usePractice();
  const { isSuperAdmin } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const location = useLocation();
  const { open: paletteOpen, closePalette } = useCommandPalette();
  const { open: drawerOpen, closeDrawer } = useAdminDrawer();
  const showDrawer = isSuperAdmin() && isNavV2Enabled() && drawerOpen;

  useEffect(() => { setMobileMenuOpen(false); }, [location.pathname]);

  return (
    <div className="App" style={styles.appLayout}>
      <CommandPalette open={paletteOpen} onClose={closePalette} />
      <AdminDrawer open={showDrawer} onClose={closeDrawer} />
      <Sidebar
        isAdminView={false}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(c => !c)}
        isMobile={isMobile}
        mobileOpen={mobileMenuOpen}
        onMobileClose={() => setMobileMenuOpen(false)}
      />
      <div style={styles.appMain}>
        <PlatformHeader isAdminView={false} isMobile={isMobile} onMenuToggle={() => setMobileMenuOpen(o => !o)} />
        <div className="container" style={styles.mainContent}>
          <AdoptionRenderer />
          {isNavV2Enabled() && <Breadcrumbs />}
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/campaigns" element={<Campaigns practiceId={selectedPracticeId} />} />
            <Route path="/waitlist" element={<WaitlistManagement practiceId={selectedPracticeId} />} />
            <Route path="/notification-center" element={<NotificationCenter practiceId={selectedPracticeId} />} />
            <Route path="/messaging" element={<Messaging practiceId={selectedPracticeId} />} />
            <Route path="/communication" element={<Communication practiceId={selectedPracticeId} />} />
            <Route path="/ai-queue" element={<AiQueue practiceId={selectedPracticeId} />} />
            <Route path="/voice-ai" element={<VoiceAi practiceId={selectedPracticeId} />} />
            {/* Legacy route — Voice Workflows is now a sub-tab inside Voice AI Settings.
                Redirect preserves old bookmarks. */}
            <Route path="/voice-ai/flows" element={<Navigate to="/practice-view/voice-ai?tab=settings" replace />} />
            <Route path="/voice-ai/flows/:flowId" element={<Navigate to="/practice-view/voice-ai?tab=settings" replace />} />
            <Route path="/reputation" element={<Reputation practiceId={selectedPracticeId} />} />
            <Route path="/patients" element={<Patients practiceId={selectedPracticeId} />} />
            <Route path="/care-hub" element={<CareHub practiceId={selectedPracticeId} />} />
            <Route path="/patient-flow" element={<PatientFlow practiceId={selectedPracticeId} />} />
            <Route path="/consents" element={<Navigate to="/practice-view/care-hub?tab=consent" replace />} />
            <Route path="/revenue-flow" element={<RevenueFlow practiceId={selectedPracticeId} />} />
            <Route path="/roi-dashboard" element={<ROIDashboard practiceId={selectedPracticeId} />} />
            <Route path="/operations-readiness" element={<OperationsReadiness />} />
            <Route path="/billing-escalations" element={<BillingEscalations practiceId={selectedPracticeId} />} />
            <Route path="/insurance-change-requests" element={<InsuranceChangeRequests practiceId={selectedPracticeId} />} />
            <Route path="/payment-settings" element={<PaymentSettings practiceId={selectedPracticeId} />} />
            <Route path="/payment-test" element={<PaymentTest practiceId={selectedPracticeId} />} />
            <Route path="/appointments" element={<Navigate to="/practice-view/care-hub?tab=calendar" replace />} />
            <Route path="/reports" element={<Reports practiceId={selectedPracticeId} />} />
            <Route path="/profile" element={<UserProfile />} />
            <Route path="/practice-settings" element={<PracticeSettings />} />
            <Route path="/tag-management" element={<TagManagement practiceId={selectedPracticeId} />} />
            <Route path="/testbed" element={<TestBed practiceId={selectedPracticeId} />} />
            <Route path="/ehr-live-test" element={<EHRLiveTest practiceId={selectedPracticeId} />} />
            <Route path="/publish-slots" element={<PublishSlots practiceId={selectedPracticeId} />} />
            <Route path="/campaign-types" element={<CampaignTypes />} />
            <Route path="/intake-dashboard" element={<Navigate to="/practice-view/care-hub?tab=intake" replace />} />
            <Route path="/ambient-scribe" element={<AmbientScribe practiceId={selectedPracticeId} />} />
            <Route path="/ambient-scribe/appointment/:appointmentId" element={<AppointmentScribe practiceId={selectedPracticeId} />} />
            <Route path="/ai-insights" element={<AiInsights practiceId={selectedPracticeId} />} />
            <Route path="/compliance" element={<Compliance practiceId={selectedPracticeId} />} />
            <Route path="/compliance/risk-assessment/:id" element={<RiskAssessmentWizard practiceId={selectedPracticeId} />} />
            <Route path="/activity" element={<ActivityLog practiceId={selectedPracticeId} />} />
            <Route path="/logs" element={<SystemLogs practiceId={selectedPracticeId} />} />
            <Route path="*" element={<Navigate to="/practice-view" replace />} />
          </Routes>
        </div>
      </div>
      <NewtonAI />
    </div>
  );
}

// Session timeout warning overlay
function SessionTimeoutWarning() {
  const { showTimeoutWarning, dismissTimeoutWarning, logout } = useAuth();
  const [secondsLeft, setSecondsLeft] = React.useState(120);

  React.useEffect(() => {
    if (!showTimeoutWarning) { setSecondsLeft(120); return; }
    const interval = setInterval(() => setSecondsLeft(s => Math.max(0, s - 1)), 1000);
    return () => clearInterval(interval);
  }, [showTimeoutWarning]);

  if (!showTimeoutWarning) return null;

  const mins = Math.floor(secondsLeft / 60);
  const secs = secondsLeft % 60;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: '32px 40px', maxWidth: 420, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>&#9201;</div>
        <h2 style={{ margin: '0 0 8px', fontSize: 20, color: '#111827' }}>Session Expiring</h2>
        <p style={{ color: '#6b7280', fontSize: 14, margin: '0 0 16px' }}>
          You will be signed out in <strong style={{ color: '#dc2626' }}>{mins}:{secs.toString().padStart(2, '0')}</strong> due to inactivity.
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button onClick={() => { dismissTimeoutWarning(); window.dispatchEvent(new Event('mousedown')); }} style={{ padding: '10px 24px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
            Stay Signed In
          </button>
          <button onClick={logout} style={{ padding: '10px 24px', background: '#f3f4f6', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, cursor: 'pointer' }}>
            Sign Out
          </button>
        </div>
      </div>
    </div>
  );
}

// Router component that decides which layout to show
function AppRouter() {
  return (
    <>
    <SessionTimeoutWarning />
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/auth/sso-callback" element={<SsoCallback />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/select-context" element={<SelectContext />} />
      <Route path="/setup" element={<Setup />} />
      <Route path="/feedback/:token" element={<FeedbackLanding />} />
      <Route path="/docs/:token" element={<DocumentUploadLanding />} />
      <Route path="/voice/:token" element={<PatientVoice />} />
      <Route path="/pay/:practiceGuid/:patientGuid" element={<PatientPayLanding />} />

      {/* Onboarding wizard - standalone full-screen (no sidebar/header) */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        }
      />

      {/* Super Admin routes — ProtectedRoute handles role checking & loading state */}
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute requiredRole="super_admin">
            <SuperAdminLayout />
          </ProtectedRoute>
        }
      />

      {/* Practice view routes - for both super admin and practice admin */}
      <Route
        path="/practice-view/*"
        element={
          <ProtectedRoute>
            <PracticeAdminLayout />
          </ProtectedRoute>
        }
      />

      {/* Default redirect based on role */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DefaultRedirect />
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<DefaultRedirect />} />
    </Routes>
    </>
  );
}

// Component to redirect based on user role
function DefaultRedirect() {
  const { isSuperAdmin, isCareProvider, isAuthenticated } = useAuth();
  const { needsOnboarding } = usePractice();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Super admins go to admin console by default
  if (isSuperAdmin()) {
    return <Navigate to="/admin" replace />;
  }

  // Practice admins with incomplete onboarding go to wizard
  if (needsOnboarding) {
    return <Navigate to="/onboarding" replace />;
  }

  // Care providers land on Patient Directory (filtered to their patients)
  if (isCareProvider()) {
    return <Navigate to="/practice-view/patients" replace />;
  }

  // Practice admins and clinical admins go to practice view dashboard
  return <Navigate to="/practice-view" replace />;
}

// Main App component with providers
function App() {
  return (
    <ErrorBoundary>
      <Router>
        <AuthProvider>
          <PracticeProvider>
            <TimezoneProvider>
              <AdoptionProvider>
                <AppRouter />
              </AdoptionProvider>
            </TimezoneProvider>
          </PracticeProvider>
        </AuthProvider>
      </Router>
    </ErrorBoundary>
  );
}

const styles = {
  appLayout: {
    minHeight: '100vh',
    display: 'flex',
    backgroundColor: '#f3f4f6'
  },
  appMain: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    overflow: 'hidden'
  },
  mainContent: {
    flex: 1,
    overflowY: 'auto'
  }
};

export default App;
